
import Vue from 'vue'
import Component from "vue-class-component";
import {Prop} from "vue-property-decorator";
import {namespace} from "vuex-class";
import ServiceProviderSearch from "@/components/serviceProvider/widget/ServiceProviderSearch.vue";
import CreateButton from "@/components/shared/CreateButton.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import {BoListPanel} from "bonomo/dist";
import {IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IProduct, IProductForm} from "@/model/Product/productInterfaces";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {IErrors} from "@/model/IErrors";
import SPNav from "@/components/serviceProvider/widget/SPNav.vue";

const serviceProviderModule = namespace("serviceProviderModule");
const productModule = namespace("productModule");

@Component({
  components: {SPNav, ServiceProviderSearch, CreateButton, PaginationBar, BoListPanel}
})
export default class ServiceProviderProducts extends Vue {
  @Prop({ default: 0 })
  protected spId!: number;

  @serviceProviderModule.Getter('serviceProviders')
  protected serviceProviders!: IServiceProvider[];
  @serviceProviderModule.Action('fetchServiceProviders')
  protected fetchServiceProviders!: () => void;

  @productModule.Action('fetchProducts')
  protected fetchProducts!: (spId: number) => any;
  @productModule.Action('fetchAllProducts')
  protected fetchAllProducts!: (params?: any) => any;
  @productModule.Action('createProduct')
  protected createProduct!: (data: { spId: number, product: IProductForm }) => any;
  @productModule.Action('updateProduct')
  protected updateProduct!: (data: { spId: number, productId: number, product: IProductForm }) => any;
  @productModule.Action('deactivateProduct')
  protected deactivateProduct!: (data: { spId: number, productId: number }) => any;
  @productModule.Getter('products')
  protected products!: IProduct[];
  @productModule.Getter('total')
  protected total!: number;
  @productModule.Getter('currentPage')
  protected currentPage!: number;
  @productModule.Getter('lastPage')
  protected lastPage!: number;
  @productModule.Getter('perPage')
  protected perPage!: number;
  @productModule.Getter('errors')
  protected errors!: IErrors;

  protected search: string = '';
  protected dialog: boolean = false;
  protected docsDialog: boolean = false;
  protected confirmDialog: boolean = false;
  protected pendingDeactivate: { spId: number, productId: number, name: string } | null = null;
  protected editingProductId: number | null = null;
  protected editingProductSpId: number | null = null;
  protected modalSpId: number | null = null;
  protected searchTimer: any = null;
  protected selectedPerPage: number = 15;

  readonly perPageOptions: number[] = [15, 30, 50];

  protected get productTypes() {
    return [{text: this.$t('products.oneTime'), value: 'one_time'}];
  }
  protected form: IProductForm = this.emptyForm();

  protected get curlDocs() {
    const auth = '-H "Authorization: Bearer {token}"';
    const json = '-H "Content-Type: application/json"';
    return [
      {
        method: 'POST',
        color: 'green darken-1',
        title: this.$t('products.createOrderTitle'),
        pending: false,
        curl: `curl -X POST "https://smartpoint.ai/api/b1/one-time-orders" \\\n  ${auth} \\\n  ${json} \\\n  -d '{\n    "products": [\n      { "public_id": "a1b2c3d4", "quantity": 1 },\n      { "public_id": "e5f6a7b8", "quantity": 2 }\n    ]\n  }'`,
      },
    ];
  }

  mounted() {
    if (!this.serviceProviders || !this.serviceProviders.length) {
      this.fetchServiceProviders();
    }
    this.loadData();
  }

  protected loadData() {
    if (this.spId) {
      this.fetchProducts(this.spId);
    } else {
      this.fetchAllProducts(this.buildFilters());
    }
  }

  protected buildFilters(): any {
    const filters: any = { per_page: this.selectedPerPage };
    if (this.search) filters.search = this.search;
    return filters;
  }

  protected onSearchInput() {
    if (this.spId) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.fetchAllProducts(this.buildFilters());
    }, 400);
  }

  protected onClearSearch() {
    if (this.spId) return;
    this.search = '';
    this.fetchAllProducts(this.buildFilters());
  }

  protected onUpdateOptions(_options: any) {
    // handled by bo-list-panel
  }

  protected noopSort(items: any[]): any[] {
    return items;
  }

  protected onPageChange(page: number) {
    if (this.spId) return;
    const filters: any = { page, per_page: this.selectedPerPage };
    if (this.search) filters.search = this.search;
    this.fetchAllProducts(filters);
  }

  protected onPerPageChange(perPage: number) {
    if (this.spId) return;
    this.selectedPerPage = perPage;
    const filters: any = { page: 1, per_page: perPage };
    if (this.search) filters.search = this.search;
    this.fetchAllProducts(filters);
  }

  protected spName(spId: number): string {
    const sp = (this.serviceProviders || []).find(s => s.id === spId);
    return sp ? sp.name : `#${spId}`;
  }

  protected get headers(): BetterDataTableHeader[] {
    const cols: BetterDataTableHeader[] = [
      {text: this.$t('products.headers.id'), value: 'id', sortable: true},
      {text: this.$t('products.headers.name'), value: 'name', sortable: true},
      {text: this.$t('products.headers.identifier'), value: 'public_id', sortable: false},
      {text: this.$t('products.headers.type'), value: 'type', sortable: true},
      {text: this.$t('products.headers.price'), value: 'price', align: 'end', sortable: true},
      {text: this.$t('products.headers.status'), value: 'active', align: 'center', sortable: true},
      {text: '', value: 'controls', sortable: false, align: 'end'},
    ];
    if (!this.spId) {
      cols.splice(1, 0, {text: this.$t('products.headers.provider'), value: 'service_provider_id', sortable: true});
    }
    return cols;
  }

  protected onModalSpSelected(spId: number | null) {
    this.modalSpId = spId || null;
  }

  protected openCreateDialog() {
    this.editingProductId = null;
    this.editingProductSpId = null;
    this.modalSpId = this.spId || null;
    this.form = this.emptyForm();
    this.dialog = true;
  }

  protected openEditDialog(product: IProduct) {
    this.editingProductId = product.id;
    this.editingProductSpId = product.service_provider_id;
    this.form = {
      name: product.name,
      description: product.description,
      price: product.price,
      type: product.type,
      active: product.active,
    };
    this.dialog = true;
  }

  protected saveProduct() {
    if (this.editingProductId) {
      this.updateProduct({
        spId: this.editingProductSpId!,
        productId: this.editingProductId,
        product: this.form,
      }).then(() => { this.dialog = false; this.loadData(); });
    } else {
      if (!this.modalSpId) return;
      this.createProduct({
        spId: this.modalSpId,
        product: this.form,
      }).then(() => { this.dialog = false; this.loadData(); });
    }
  }

  protected get pendingDeactivateName(): string {
    if (!this.pendingDeactivate) return '';
    return this.pendingDeactivate.name;
  }

  protected requestDeactivate(item: IProduct) {
    this.pendingDeactivate = { spId: item.service_provider_id, productId: item.id, name: item.name };
    this.confirmDialog = true;
  }

  protected confirmDeactivate() {
    if (!this.pendingDeactivate) return;
    this.deactivateProduct(this.pendingDeactivate).then(() => {
      this.confirmDialog = false;
      this.pendingDeactivate = null;
      this.loadData();
    });
  }

  protected copyPublicId(publicId: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(publicId);
    }
  }

  protected copyText(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  }

  private emptyForm(): IProductForm {
    return {name: '', description: null, price: 0, type: 'one_time', active: true};
  }
}
